<template>
  <div class="data-card card" data-step="8">
    <div class="data-container">
      <div class="title">
        Liste des talents activables pour cette campagne
      </div>
      <p>
        Sur Yuzu, vos talents ne sont pas activés par défaut. Vous pouvez
        faire le choix de les activer à tout moment depuis ce tableau.
      </p>
      <Table
        :data="participantStore.toActiveParticipants"
        :fields="state.fields"
        :search="search"
        :bulk="
          !campaignStore.currentCampaign.archived
            ? {
                actions: [
                  {
                    icon: 'bi-plus-square',
                    class: 'primary',
                    label: 'Activer les évaluations',
                    action: (participants) => {
                      openActionModal([...participants], 'activate');
                    },
                    display: () => {
                      return true;
                    },
                  },
                  {
                    icon: 'bi-arrow-left-right',
                    label: 'Déplacer les talents',
                    class: 'secondary outline',
                    action: (participants) => {
                      openActionModal([...participants], 'move');
                    },
                    display: () => {
                      return true;
                    },
                  },
                  {
                    class: 'red',
                    icon: 'bi-trash',
                    label: 'Supprimer les talents',
                    action: (participants) => {
                      openActionModal([...participants], 'delete');
                    },
                    display: () => {
                      return true;
                    },
                  },
                ],
              }
            : null
        "
        @clickOnRow="openParticipantModal"
      >
        <template #[`candidate.last_name`]="{ data }">
          <span v-if="data.candidate_id"
            >{{ data.candidate.first_name }} {{ data.candidate.last_name }}
          </span>
          <span v-else>
            <p
              v-if="!data.pending_first_name || !data.pending_last_name"
              class="muted"
            >
              Non renseigné
            </p>
            <template v-else>
              {{ data.pending_first_name }} {{ data.pending_last_name }}
            </template>
          </span>
        </template>
        <template #[`candidate.email`]="{ data }">
          <span v-if="data.candidate_id" v-text="data.candidate.email" />
          <span v-else v-text="data.pending_email" />
        </template>
        <template #creation_time="{ data }">
          <div
            :style="{ width: 'fit-content' }"
            v-text="getDate(data.creation_time, 'D MMM YYYY')"
            v-tooltip="{
              content: getDate(data.creation_time, 'D MMM YYYY à HH[h]mm'),
              placement: 'top',
            }"
          />
        </template>
        <template #candidate_id="{ data }">
          <div
            v-if="data.candidate"
            class="tag purple"
            v-text="'Compte créé'"
            v-tooltip="
              'Ce talent a créé son compte et attends une activation pour commencer son évaluation'
            "
          />
          <div
            v-else
            class="tag grey-light"
            v-text="'Compte non créé'"
            v-tooltip="'Ce talent n’a pas encore créé son compte Yuzu'"
          />
        </template>
        <template #actions="actionsProps">
          <Button
            label="Activer l'évaluation"
            class="primary icon"
            icon="bi-plus-square"
            data-step="9"
            @click="openActionModal([actionsProps.row], 'activate')"
            :disabled="campaignStore.currentCampaign.archived"
          />
          <div @click.stop="null">
            <VDropdown
              :distance="10"
              :disabled="campaignStore.currentCampaign.archived"
              placement="left"
            >
              <div
                class="btn outline secondary icon square"
                :class="{ disabled: campaignStore.currentCampaign.archived }"
              >
                <i class="bi bi-three-dots-vertical" />
              </div>
              <template #popper>
                <div
                  class="action red-color"
                  v-close-popper
                  @click="openActionModal([actionsProps.row], 'delete')"
                >
                  <i class="bi bi-trash" />
                  <span>Supprimer le talent</span>
                </div>
                <div class="separator" />
                <div
                  class="action"
                  v-close-popper
                  @click="openActionModal([actionsProps.row], 'move')"
                >
                  <i class="bi bi-arrow-left-right" />
                  <span>Déplacer le talent</span>
                </div>
              </template>
            </VDropdown>
          </div>
        </template>
        <template #empty>
          <Empty
            message="<b>Aucune évaluation</b> à activer pour le moment"
            :action="
              !campaignStore.currentCampaign.archived
                ? {
                    label: 'Ajouter des talents à la campagne',
                    icon: 'bi-plus-lg',
                    routeParams: {
                      name: 'addCandidates',
                      params: {
                        campaignId:
                          route.params.campaignId ||
                          '8d9740ed-56ac-4f1f-856f-df9d81f16c52',
                      },
                    },
                  }
                : null
            "
            :dark="isDark"
            icon="folder"
          />
        </template>
      </Table>
      <ParticipantModal
        :isOpen="state.showParticipantModal"
        @close="state.showParticipantModal = !state.showParticipantModal"
      />
      <ActionModal
        :modal="state.actionModal.modal"
        :data="state.participants"
        :isOpen="state.actionModal.show"
        @close="state.actionModal.show = false"
        @confirm="state.actionModal.show = false"
      />
    </div>
  </div>
</template>

<script setup>
import { reactive, onMounted } from "vue";
import { storeToRefs } from "pinia";
import { useCampaignStore } from "@/stores/campaign.js";
import { useParticipantStore } from "@/stores/participant.js";
import { useOnboardingStore } from "@/stores/onboarding.js";
import { useRoute } from "vue-router";
import { useDark } from "@vueuse/core";
import { getDate, dateIsBefore } from "@/composables/utils.js";
import ActionModal from "@/components/modals/app/actionModal/ActionModal.vue";
import ParticipantModal from "@/components/modals/app/participantModal/ParticipantModal.vue";
import Empty from "@/components/table/Empty.vue";

const isDark = useDark();

const route = useRoute();

const campaignStore =
  route.name === "campaign" ? useCampaignStore() : useOnboardingStore();

const participantStore =
  route.name === "campaign" ? useParticipantStore() : useOnboardingStore();

const { currentParticipant } = storeToRefs(participantStore);

defineProps({
  search: {
    type: Object,
  },
});

const state = reactive({
  showParticipantModal: false,
  actionModal: {
    show: false,
    modal: "",
  },
  activateModalIsLoading: false,
  participants: [],
  fields: [
    {
      id: "candidate.last_name",
      fallback: "pending_last_name",
      label: "Talent",
      sort(a, b, dir) {
        if (dir === "ASC") return a.localeCompare(b);
        return b.localeCompare(a);
      },
    },
    {
      id: "candidate.email",
      fallback: "pending_email",
      label: "Adresse e-mail",
      sort(a, b, dir) {
        if (dir === "ASC") return a.localeCompare(b);
        return b.localeCompare(a);
      },
    },
    {
      id: "creation_time",
      label: "Date d'ajout",
      defaultSortDir: "DESC",
      sort(a, b, dir) {
        if (dir === "ASC") {
          return dateIsBefore(b, a) ? 1 : -1;
        } else {
          return dateIsBefore(a, b) ? 1 : -1;
        }
      },
    },
    {
      id: "candidate_id",
      label: "Statut",
      sort(a, b, dir) {
        if (dir === "ASC") return (typeof a).localeCompare(typeof b);
        return (typeof b).localeCompare(typeof a);
      },
    },
  ],
});

function openParticipantModal(participant) {
  currentParticipant.value = participant;
  state.showParticipantModal = true;
}

function openActionModal(participants, modal) {
  state.participants = participants;
  state.actionModal = {
    show: true,
    modal,
  };
}

onMounted(() => {
  if (localStorage.getItem("pending_activations")) {
    const pending_activations = JSON.parse(
      localStorage.getItem("pending_activations")
    );

    if (pending_activations.campaign_id === route.params.campaignId) {
      openActionModal(pending_activations.participants, "activate");
    }
  }
});
</script>
